import { useEffect, useRef } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

export function Reveal({ children, delay = 0, y = 28, className = "", once = true }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, amount: 0.15 }}
      transition={{ duration: 0.7, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

export function KineticHeading({ lines, className = "", lineClassName = "", as: Tag = "h1", testid }) {
  return (
    <Tag className={className} data-testid={testid}>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
          <motion.span
            className={`block will-change-transform ${lineClassName}`}
            initial={{ y: "115%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.95, delay: 0.12 + i * 0.13, ease: EASE }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </Tag>
  );
}

export function CountUp({ to, suffix = "", decimals = 0, className = "", testid }) {
  const numRef = useRef(null);
  const mv = useMotionValue(0);
  const spring = useSpring(mv, { duration: 1900, bounce: 0 });

  useEffect(
    () =>
      spring.on("change", (v) => {
        if (numRef.current) {
          numRef.current.textContent =
            decimals > 0
              ? v.toLocaleString("en-CA", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
              : Math.round(v).toLocaleString("en-CA");
        }
      }),
    [spring, decimals]
  );

  return (
    <motion.span
      className={className}
      data-testid={testid}
      onViewportEnter={() => mv.set(to)}
      viewport={{ once: true, amount: 0.4 }}
    >
      <span ref={numRef}>0</span>
      {suffix}
    </motion.span>
  );
}

export function Marquee({ items }) {
  const seq = (hidden) => (
    <div className="marquee-seq" aria-hidden={hidden || undefined}>
      {items.map((it, i) => (
        <span key={i} className="marquee-item">
          <span className="marquee-dot" />
          {it}
        </span>
      ))}
    </div>
  );
  return (
    <div className="marquee bg-ink" data-testid="marquee-ribbon">
      <div className="marquee-track">
        {seq(false)}
        {seq(true)}
      </div>
    </div>
  );
}

export function TiltCard({ children, className = "", max = 7 }) {
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 160, damping: 18 });
  const sry = useSpring(ry, { stiffness: 160, damping: 18 });

  return (
    <motion.div
      className={className}
      style={{ rotateX: srx, rotateY: sry, transformPerspective: 1000 }}
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        ry.set(((e.clientX - r.left) / r.width - 0.5) * max);
        rx.set(-((e.clientY - r.top) / r.height - 0.5) * max);
      }}
      onMouseLeave={() => {
        rx.set(0);
        ry.set(0);
      }}
    >
      {children}
    </motion.div>
  );
}
